import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import API, { setAccessToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Handles the Emergent Google OAuth callback for provider SSO.
 *
 * Flow:
 *   1. User clicks "Continue with Google" on /provider-login
 *   2. They are sent to auth.emergentagent.com, sign in with Google
 *   3. Emergent redirects back to /provider-auth-callback#session_id=XXXXX
 *   4. This page reads the session_id from the URL fragment, POSTs to our
 *      backend which exchanges it for our JWT (and enforces the
 *      @elevatingmindspsychiatry.com domain allowlist).
 *   5. On success: navigates to /provider. On failure: back to /provider-login.
 */
export default function ProviderAuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    // useRef flag (not useState) to survive StrictMode double-mount per the playbook
    if (handled.current) return;
    handled.current = true;

    (async () => {
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      const match = hash.match(/session_id=([^&]+)/);
      if (!match) {
        navigate("/provider-login?sso=missing", { replace: true });
        return;
      }
      const session_id = decodeURIComponent(match[1]);

      try {
        const { data } = await API.post("/auth/provider-google", { session_id });
        if (data?.access_token) setAccessToken(data.access_token);
        // Clean the URL fragment so session_id isn't retained in history
        if (window.history?.replaceState) {
          window.history.replaceState(null, "", "/provider");
        }
        await checkAuth();
        navigate("/provider", { replace: true });
      } catch (err) {
        const code = err?.response?.status === 403 ? "domain" : "error";
        const detail = err?.response?.data?.detail;
        const qs = new URLSearchParams({ sso: code, msg: typeof detail === "string" ? detail : "" });
        navigate(`/provider-login?${qs.toString()}`, { replace: true });
      }
    })();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="provider-auth-callback">
      <div className="text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Finishing Google sign-in…</p>
      </div>
    </div>
  );
}
